/** 작업 이력 찾기 조건. 목업 `A-44`·`A-45`·`A-45b`·`A-46`·`A-47` 와 `W-55`·`W-55b` 가
 * 같은 조건을 쓴다. 폰은 시트로 네 화면에 나눠 담고 웹은 한 화면에 펴지만, **고르는 값과
 * 판정은 같아야 한다**. 앱에서 건 조건이 웹에서 다르게 걸리면 넘나들기가 깨진다.
 *
 * 서버 쪽 대응은 `apps/api/src/lib/jobs.ts` 의 `listJobsForSeller(filter)` 이고 그 함수는
 * 이미 상태·올린 곳·기간을 받는다. **`GET /api/jobs` 라우트도 이제 이 값을 다 읽는다**
 * (`apps/api/src/routes/jobs.ts` 의 `parseStatusTokens`·`parseDateRange`). 예전 주석이
 * "라우트가 `limit`·`offset` 만 파싱한다"고 적어 둔 것은 낡았다. 사실을 반대로 말하고
 * 있었다. 그래서 조건은 `jobEndpoints.list` 가 질의 문자열로 만든다.
 */

import type { DetailJobStatus, JobOrigin } from "./domain";

/** 기간 프리셋. **기간은 하나만 고른다**(`A-45` 제목 줄 `기간 · 하나만`).
 *
 * `CUSTOM` 은 `A-45b` 달력이 만든다. 프리셋은 "최근"만 답할 수 있어 "작년 3월"을 가리키지
 * 못하고, 결제 줄 수가 곧 작업 수라 반년이면 백 줄이 넘는다. */
export type DatePresetKey = "TODAY" | "D7" | "D30" | "M3" | "ALL" | "CUSTOM";

export interface DatePresetOption {
  key: Exclude<DatePresetKey, "CUSTOM">;
  /** 목업 문구 그대로. */
  label: string;
  /** **오늘을 포함해 며칠인가.** `오늘`=1, `7일`=7. `ALL` 은 `null`.
   *
   * 예전 이름은 `days` 였고 뜻이 "오늘로부터 며칠 전"이었다. 그러면 `7일` 칩이 실제로
   * **8일 구간**을 가리킨다(`오늘 - 7일 00:00` 부터 오늘까지 세면 여드레). `30일`·`3개월`
   * 도 같았다. 양 끝을 포함해 세는 `customRangeDayCount` 의 규약과도 어긋났다.
   * 이름을 바꾼 것은 뜻이 바뀐 값을 옛 이름으로 두면 호출부가 조용히 옛 뜻으로 읽기
   * 때문이다. */
  dayCount: number | null;
}

/** `A-45`·`W-55b` 의 기간 칩. 순서도 목업 그대로다.
 *
 * `3개월` 을 90일로 두는 것은 근사다. 달을 세면 2월을 지나는 3개월과 여름 3개월의 날수가
 * 달라 셀러가 같은 칩에서 다른 길이를 받는다. 정확한 달 경계가 필요하면 `CUSTOM` 이 있다. */
export const DATE_PRESETS: readonly DatePresetOption[] = [
  { key: "TODAY", label: "오늘", dayCount: 1 },
  { key: "D7", label: "7일", dayCount: 7 },
  { key: "D30", label: "30일", dayCount: 30 },
  { key: "M3", label: "3개월", dayCount: 90 },
  { key: "ALL", label: "전체", dayCount: null },
] as const;

/** 걸린 조건 칩(`A-46` 의 `3개월 ✕`)이 쓰는 말. `CUSTOM` 은 고른 날짜가 라벨이 된다. */
export function datePresetLabel(key: DatePresetKey): string {
  return DATE_PRESETS.find((p) => p.key === key)?.label ?? "직접 고른 기간";
}

/** 상태 칩 하나. **칩 하나가 상태 여러 개를 가리킬 수 있다**. 셀러에게 `만드는 중`은 한
 * 낱말이지만 서버는 결제만 끝난 것과 실제로 도는 것을 갈라 들고 있다. 그 사정을 화면에
 * 내보내지 않으려면 매핑이 여기 있어야 한다. */
export interface JobStatusFilterOption {
  key: string;
  /** 화면 문구. `job-labels.ts` 의 칩 라벨과 같은 낱말을 쓴다. */
  label: string;
  statuses: readonly DetailJobStatus[];
}

/** `A-45` 의 `상태 · 여러 개`. 목업이 그린 넷만 둔다.
 *
 * `사진 지워짐`(`DISCARDED`)을 칩으로 두지 않은 것은 목업의 판단이다. 목록에는 남지만
 * 골라 찾는 값은 아니다. `만드는 중`도 `A-45` 에 없다. 지난 것을 되짚는 화면이라
 * 지금 도는 작업은 홈(`A-10`)의 일이다. */
export const JOB_STATUS_FILTERS: readonly JobStatusFilterOption[] = [
  { key: "EXPORTED", label: "내보냄", statuses: ["EXPORTED"] },
  { key: "FAILED", label: "실패", statuses: ["FAILED"] },
  { key: "READY", label: "준비됨", statuses: ["READY"] },
  { key: "DRAFT", label: "초안", statuses: ["UPLOADED"] },
] as const;

export interface JobOriginFilterOption {
  key: JobOrigin;
  label: string;
}

/** `A-45` 의 `올린 곳 · 여러 개`. `UNKNOWN` 은 0002 이전 행 전용이라 칩으로 두지 않는다. */
export const JOB_ORIGIN_FILTERS: readonly JobOriginFilterOption[] = [
  { key: "APP", label: "앱" },
  { key: "WEB", label: "웹" },
] as const;

/** 화면이 들고 있는 조건. **비어 있음 = 안 걸림**이다. `statusKeys` 가 빈 배열이면 전체다. */
export interface JobSearchFilter {
  period: DatePresetKey;
  /** `period === "CUSTOM"` 일 때만 쓴다. `YYYY-MM-DD` 로컬 날짜. */
  customFrom?: string;
  customTo?: string;
  statusKeys: string[];
  origins: JobOrigin[];
}

export const EMPTY_JOB_SEARCH_FILTER: JobSearchFilter = {
  period: "ALL",
  statusKeys: [],
  origins: [],
};

/** 걸린 조건의 개수. `A-44` 내비의 `조건 3개`와 `A-46` 칩 줄이 같은 숫자를 써야 한다.
 *  기간이 `ALL` 이면 안 건 것이다. */
export function activeFilterCount(filter: JobSearchFilter): number {
  const period = filter.period === "ALL" ? 0 : 1;
  return period + filter.statusKeys.length + filter.origins.length;
}

export function isFilterActive(filter: JobSearchFilter): boolean {
  return activeFilterCount(filter) > 0;
}

/** 칩 키 → 서버가 아는 상태값. 중복은 걷어낸다. */
export function resolveStatuses(filter: JobSearchFilter): DetailJobStatus[] {
  const out = new Set<DetailJobStatus>();
  for (const key of filter.statusKeys) {
    const option = JOB_STATUS_FILTERS.find((o) => o.key === key);
    if (option) for (const s of option.statuses) out.add(s);
  }
  return [...out];
}

// ── 기간 경계 ────────────────────────────────────────────────────────────────
//
// **셀러가 말하는 `오늘`은 셀러의 로컬 날짜다. 저장은 UTC 다**(`createdAt` 이
// `CURRENT_TIMESTAMP`). 그래서 경계는 반드시 **로컬 자정을 UTC 로 옮긴 값**이어야 한다.
//
// 예전 판은 `setUTCHours(0, 0, 0, 0)` 로 UTC 자정을 썼고 `customFrom`·`customTo` 도 로컬
// 날짜라 적어 두고서 그대로 UTC 문자열로 만들었다. 그러면 **KST 오전 8시에 만든 작업은 UTC
// 로 어제 23시라 `오늘` 칩에서 사라진다**(9시간 밀림). 게다가 앱은 로컬 자정으로 걸러서
// 같은 `9월 8일` 이 앱과 웹에서 다른 결과를 냈다. 이 파일 머리말이 막겠다고 적어 둔 바로
// 그 상황이다. 예전 주석이 남겨 둔 탈출구("로컬 자정으로 자르는 것은 `now` 를 넘기는 쪽이
// 정한다")는 성립하지 않았다. `setUTCHours` 는 어떤 `now` 를 받아도 UTC 자정으로 뭉갠다.
//
// **시간대를 어디서 얻는가는 아직 결정이 아니다**(기기 시간대인가 계정 설정인가.
// `docs/DATA_DICTIONARY.md` 8장 오픈 이슈). 지금은 **기기 시간대**로 둔다. 계정 설정으로
// 옮기더라도 바뀌는 것은 `offsetMinutes` 를 채우는 쪽이고 이 함수는 그대로다.

const MS_PER_MINUTE = 60_000;
const MS_PER_DAY = 86_400_000;
const LOCAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** 기간 경계를 만들 때 필요한 두 값. **앱·웹·서버가 같은 함수를 쓰게 하려고 인자로 뺐다.** */
export interface DateRangeContext {
  /** 기준 시각. 기본은 호출 시점. */
  now?: Date;
  /** 셀러 시간대의 UTC 기준 오프셋(분). KST = `540`. 인도처럼 30분 단위도 값이 그대로 든다.
   *
   * 기본값은 **이 코드가 도는 기기의 오프셋**이다. 앱과 웹은 셀러 기기에서 돌아 그 기본값이
   * 맞다. **서버(Workers)는 UTC 라 기본값이 0 이다**. 라우트가 날짜만 받은 경우
   * `JOB_SEARCH_PARAMS.tzOffset` 을 읽어 이 값으로 넘겨야 한다. 안 넘기면 셀러의 `9월 8일`
   * 이 UTC 9월 8일로 읽혀 앞 9시간이 빠진다. */
  offsetMinutes?: number;
}

/** 이 코드가 도는 기기의 UTC 기준 오프셋(분). KST 에서 `540`.
 *
 * `Date.prototype.getTimezoneOffset()` 은 부호가 반대(UTC - 로컬)라 뒤집어 돌려준다.
 * `at` 을 받는 것은 일광절약시간 때문이다. 오프셋은 시점마다 다르다. */
export function deviceUtcOffsetMinutes(at: Date = new Date()): number {
  return -at.getTimezoneOffset();
}

interface LocalDateParts {
  year: number;
  /** 0-based. `Date.UTC` 와 같은 규약이다. */
  month: number;
  day: number;
}

/** `at` 시점에 셀러 벽시계가 가리키는 연·월·일. */
function localDateParts(at: Date, offsetMinutes: number): LocalDateParts {
  const shifted = new Date(at.getTime() + offsetMinutes * MS_PER_MINUTE);
  return {
    year: shifted.getUTCFullYear(),
    month: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
  };
}

/** 로컬 자정의 UTC 시각(ms). `dayShift` 로 날짜를 민다.
 *
 * `Date.UTC` 가 날짜 넘침을 대신 계산한다. `day + 1` 이 달 끝을 넘거나 `day - 6` 이 1일
 * 앞으로 가도, 윤년 2월 29일이 끼어도 달력이 맞는다. */
function localMidnightUtcMs(
  parts: LocalDateParts,
  offsetMinutes: number,
  dayShift = 0,
): number {
  return Date.UTC(parts.year, parts.month, parts.day + dayShift) - offsetMinutes * MS_PER_MINUTE;
}

/** `YYYY-MM-DD HH:MM:SS` (UTC). D1 의 `CURRENT_TIMESTAMP` 와 같은 모양이라 문자열로 비교된다. */
function toServerTimestamp(ms: number): string {
  const at = new Date(ms);
  const p = (n: number) => (n < 10 ? `0${n}` : `${n}`);
  return (
    `${at.getUTCFullYear()}-${p(at.getUTCMonth() + 1)}-${p(at.getUTCDate())} ` +
    `${p(at.getUTCHours())}:${p(at.getUTCMinutes())}:${p(at.getUTCSeconds())}`
  );
}

/** `YYYY-MM-DD` 로컬 날짜 문자열 → 연·월·일. 형식이 틀리거나 없으면 `null`. */
function parseLocalDate(value: string | undefined): LocalDateParts | null {
  if (!value) return null;
  const m = LOCAL_DATE_RE.exec(value);
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) };
}

/** 프리셋·달력 → 조회 구간. 돌려주는 두 값은 **UTC** `YYYY-MM-DD HH:MM:SS` 다
 * (`format.ts` 의 `parseServerDate` 와 같은 규약). 서버는 `createdAt >= from` ·
 * `createdAt <= to` 로 비교한다(`apps/api/src/lib/jobs.ts`).
 *
 * **끝은 그날 끝까지 포함한다.** `createdAt <= '2026-09-08'` 로 보내면 그날 만든 작업이
 * 통째로 빠진다. 끝값은 다음 로컬 자정에서 1초를 뺀 시각이다. `createdAt` 이 초 단위라
 * 이 경계가 정확하다.
 *
 * 프리셋의 `to` 는 `null` 이다. 위쪽이 열려 있어도 미래에 만들어진 작업은 없다.
 *
 * 오프셋은 `now` 시점 값 하나로 구간 전체를 계산한다. 3개월 구간 안에 일광절약 전환이
 * 끼면 그 경계가 한 시간 어긋날 수 있다. KST 는 전환이 없고, 있는 지역에서도 목록 한
 * 화면의 경계라 그 한 시간을 시간대 데이터베이스를 들고 오는 값과 바꾸지 않는다. */
export function resolveDateRange(
  filter: JobSearchFilter,
  context: DateRangeContext = {},
): { from: string | null; to: string | null } {
  const now = context.now ?? new Date();
  const offsetMinutes = context.offsetMinutes ?? deviceUtcOffsetMinutes(now);

  if (filter.period === "CUSTOM") {
    const fromParts = parseLocalDate(filter.customFrom);
    const toParts = parseLocalDate(filter.customTo);
    return {
      from: fromParts ? toServerTimestamp(localMidnightUtcMs(fromParts, offsetMinutes)) : null,
      // 끝일 다음 자정 - 1초.
      to: toParts
        ? toServerTimestamp(localMidnightUtcMs(toParts, offsetMinutes, 1) - 1000)
        : null,
    };
  }

  const option = DATE_PRESETS.find((p) => p.key === filter.period);
  if (!option || option.dayCount === null) return { from: null, to: null };

  // 오늘을 포함해 `dayCount` 일이므로 시작은 `dayCount - 1` 일 전 로컬 자정이다.
  const today = localDateParts(now, offsetMinutes);
  return {
    from: toServerTimestamp(localMidnightUtcMs(today, offsetMinutes, -(option.dayCount - 1))),
    to: null,
  };
}

/** 고른 기간의 날수. `A-45b` 가 `31일`로 한 번 더 적는다. 달력에서 칠해진 칸을 세는 것보다
 *  숫자가 빠르다. 양 끝을 포함해 센다.
 *
 * **여기에는 시간대가 없다.** 두 날짜 문자열 사이의 달력 칸 수를 세는 일이라 어느 시간대의
 * 자정인지가 답을 바꾸지 않는다. 그래서 양쪽을 UTC 자정으로 읽는다. */
export function customRangeDayCount(from: string, to: string): number {
  const start = parseLocalDate(from);
  const end = parseLocalDate(to);
  if (!start || !end) return 0;
  const startMs = Date.UTC(start.year, start.month, start.day);
  const endMs = Date.UTC(end.year, end.month, end.day);
  if (endMs < startMs) return 0;
  return Math.round((endMs - startMs) / MS_PER_DAY) + 1;
}

/** `GET /api/jobs` 의 질의 파라미터 이름. **서버가 이 값을 다 읽는다**
 * (`apps/api/src/routes/jobs.ts`. 모르는 토큰은 무시하지 않고 400 으로 거절한다).
 * 이름을 한자리에 못 박아 두는 것은 앱·웹·서버가 다른 이름을 지어내지 않게 하기 위해서다.
 * 질의 문자열은 손으로 조립하지 않고 `jobEndpoints.list` 가 만든다.
 *
 * `tzOffset` 은 **아직 라우트가 읽지 않는다**(coding-agent 소관). 이름을 지금 박아 두는
 * 이유는 아래 `parseUtcOffsetMinutes` 설명에 있다. */
export const JOB_SEARCH_PARAMS = {
  status: "status",
  origin: "origin",
  from: "from",
  to: "to",
  /** 셀러 시간대의 UTC 기준 오프셋(분). `540` = KST. */
  tzOffset: "tzOffset",
  limit: "limit",
  offset: "offset",
  /** 줄마다 대표 축소본 주소(`JobListItemView.thumbUrl`)를 실어 달라는 표시. **정확히 `"1"` 만
   *  켠다.** 그 밖의 값과 없음은 꺼진 것이고 400 이 아니다(부가 정보라 요청을 막을 이유가 없다).
   *  꺼져 있으면 서버는 그 칸을 싣지 않고 추가 질의도 내지 않는다(`docs/DATA_DICTIONARY.md` 52장). */
  thumbs: "thumbs",
} as const;

/** `tzOffset` 파라미터 → 분. 범위를 벗어나거나 정수가 아니면 `null`.
 *
 * **라우트가 날짜만(`from=2026-09-08`) 받은 경우에만 필요하다.** 앱과 웹은
 * `jobEndpoints.list` 를 거쳐 이미 시각까지 자른 UTC 값을 보내므로 서버가 시간대를 몰라도
 * 된다. 그런데 라우트는 날짜만 온 경우도 받아 주고(`DATE_ONLY_RE`), 그때
 * `resolveDateRange` 를 시간대 없이 부르면 **Workers 는 UTC 라 셀러의 `9월 8일` 이 UTC
 * 9월 8일로 읽히고 앞 9시간이 빠진다.** 그 자리를 막는 값이다.
 *
 * ±14시간은 IANA 시간대의 실제 상한(UTC+14 = 키리바시 라인 제도)이다. */
export function parseUtcOffsetMinutes(raw: string | undefined): number | null {
  if (raw === undefined || raw === "") return null;
  const parsed = Number(raw);
  if (!Number.isInteger(parsed) || Math.abs(parsed) > 14 * 60) return null;
  return parsed;
}

/** `DetailJobStatus` 의 런타임 목록. 문자열 유니온은 컴파일하면 사라져 목록이 따로 필요하다.
 *
 * **여기 있는 이유**: `apps/api/src/routes/jobs.ts` 가 같은 목록(`KNOWN_JOB_STATUSES`)을
 * 손으로 들고 있었다. 정본이 둘이면 `domain.ts` 에 상태를 더한 사람이 한쪽만 고치고, 그러면
 * 서버가 새 상태를 "모르는 토큰"으로 400 거절한다. 양쪽이 이 목록을 읽는다.
 *
 * 아래 `Record` 가 빠짐을 컴파일에서 막는다. `domain.ts` 에 값을 더하면 키가 모자라
 * `pnpm typecheck` 가 멈춘다. 주석으로 "여기도 더해라"라고 적어 두는 것과 다르다. */
const JOB_STATUS_PRESENCE: Record<DetailJobStatus, true> = {
  UPLOADED: true,
  PAID: true,
  PROCESSING: true,
  READY: true,
  EXPORTED: true,
  FAILED: true,
  DISCARDED: true,
};

export const ALL_JOB_STATUSES = Object.keys(JOB_STATUS_PRESENCE) as DetailJobStatus[];

export function isJobStatus(value: string): value is DetailJobStatus {
  return Object.prototype.hasOwnProperty.call(JOB_STATUS_PRESENCE, value);
}

/** `JobOrigin` 의 런타임 목록. `UNKNOWN` 이 들어 있다. 칩(`JOB_ORIGIN_FILTERS`)에는 없지만
 *  0002 이전 행이 그 값을 들고 있어 **서버는 받아야 한다**. 두 목록이 다른 것이 맞다. */
const JOB_ORIGIN_PRESENCE: Record<JobOrigin, true> = {
  APP: true,
  WEB: true,
  UNKNOWN: true,
};

export const ALL_JOB_ORIGINS = Object.keys(JOB_ORIGIN_PRESENCE) as JobOrigin[];

export function isJobOrigin(value: string): value is JobOrigin {
  return Object.prototype.hasOwnProperty.call(JOB_ORIGIN_PRESENCE, value);
}
